/**
 * The rail's chapter icons, by name — ADR 9004's amendment, audit v4 G-15.
 *
 * A manifest names its icon (`FeatureManifest.icon`, one of `CHAPTER_ICONS`) and never
 * imports an icon set; the shell resolves the name here. `Record<ChapterIcon, …>` makes this
 * exhaustive, so a name added to `CHAPTER_ICONS` without its component is a compile error.
 *
 * The set is the brand guide's (p.14, "Module icons"), not the mockup's. The first icon rail
 * took the mockup's simplified paths (`layers`, `calendar`, `flag`, `inbox`, `archive`,
 * `history`), which gave In-season the Workbench's `inbox` and disagreed with the guide on
 * every chapter; audit v5 N-06 pointed at the guide, and ADR 9004's second icon amendment
 * adopts it whole.
 */

import {
  Activity, ChartLine, Circle, ClipboardList, Database, HeartPulse, Megaphone, PackagePlus, Percent, Shrink, SlidersHorizontal, Split, Tag,
  Telescope, TrendingUp,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { ChapterIcon } from '../../features/manifest';
import type { RailGroupId } from './rail-layout';

export const RAIL_ICONS: Readonly<Record<ChapterIcon, LucideIcon>> = {
  database: Database,
  'trending-up': TrendingUp,
  'clipboard-list': ClipboardList,
  split: Split,
  activity: Activity,
  percent: Percent,
  'chart-line': ChartLine,
};

/**
 * What a chapter with no declared icon shows. Neutral on purpose: borrowing another chapter's
 * icon would make two chapters look like one in the only view where the icon is all there is.
 */
export const RAIL_ICON_FALLBACK: LucideIcon = Circle;

/**
 * One icon per rail group — ADR 9023. The collapsed rail shows one icon per app, not per
 * chapter, and the wide rail heads each dropdown with the same one. Exhaustive over
 * `RailGroupId`, so an app added to `RAIL_APP_IDS` without its icon is a compile error.
 */
export const RAIL_GROUP_ICONS: Readonly<Record<RailGroupId, LucideIcon>> = {
  pricing: Tag,
  promotions: Megaphone,
  replenishment: PackagePlus,
  compacting: Shrink,
  // bata-reqs ADR 9003, `docs/adr/9079`: the Health app.
  health: HeartPulse,
  management: SlidersHorizontal,
  future: Telescope,
};
